"use client"

import posthog from "posthog-js"
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react"
import { Suspense, useEffect, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { capturePosthogEvent } from "@/lib/posthog"
import { consumeCrossDomainDistinctId } from "@/lib/posthog-cross-domain"

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_API_HOST ||
  process.env.NEXT_PUBLIC_POSTHOG_HOST ||
  "/ingest"

// posthog-js 1.372 changed the `loaded` callback parameter type to a smaller
// PostHogInterface. We just stash whatever it gives us on window for dev
// console access; the precise type isn't load-bearing.
type PostHogWindow = typeof window & {
  posthog?: unknown
  __posthogInitialized?: boolean
  __posthogErrorHandlersAttached?: boolean
}

function attachGlobalErrorHandlers(win: PostHogWindow) {
  if (win.__posthogErrorHandlersAttached) return
  win.__posthogErrorHandlersAttached = true

  const onError = (event: ErrorEvent) => {
    try {
      const err = event.error instanceof Error ? event.error : new Error(event.message || "Unknown error")
      posthog.captureException(err, {
        source: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    } catch {
      // never let analytics break the page
    }
  }

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason
      const err = reason instanceof Error ? reason : new Error(typeof reason === "string" ? reason : JSON.stringify(reason))
      posthog.captureException(err, {
        source: "window.onunhandledrejection",
      })
    } catch {
      // never let analytics break the page
    }
  }

  win.addEventListener("error", onError)
  win.addEventListener("unhandledrejection", onUnhandledRejection)
}

/**
 * Tracks client-side navigation changes and records PostHog pageviews.
 * Uses PostHog directly (instead of autocapture) so we get consistent,
 * SPA-friendly analytics with referrers intact.
 */
function PostHogPageviewTracker() {
  const posthogClient = usePostHog()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === "undefined" || !posthogClient) return

    // Stitch tryvotely.com -> votelyquiz.com identity BEFORE the first
    // $pageview fires, so the pageview itself is attributed to the source
    // person instead of the fresh anon distinct_id. No-op when there is
    // no ph_did param in the URL.
    consumeCrossDomainDistinctId(posthogClient)

    const url = `${window.location.pathname}${window.location.search}`
    if (previousUrlRef.current === url) return

    const fullUrl = window.location.href
    const referrer = document.referrer || previousUrlRef.current
    let referringDomain: string | undefined

    if (referrer) {
      try {
        referringDomain = new URL(referrer).hostname
      } catch {
        referringDomain = undefined
      }
    }

    capturePosthogEvent(posthogClient, "$pageview", {
      $current_url: fullUrl,
      $pathname: window.location.pathname,
      $referrer: referrer || undefined,
      $referring_domain: referringDomain,
    })

    previousUrlRef.current = url
  }, [pathname, searchParams, posthogClient])

  return null
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === "undefined") return

    // Skip PostHog on /jobs pages
    if (pathname?.startsWith("/jobs")) return

    const posthogWindow = window as PostHogWindow

    if (!POSTHOG_KEY) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[PostHog] NEXT_PUBLIC_POSTHOG_KEY is missing; analytics will stay disabled.")
      }
      return
    }

    if (posthogWindow.__posthogInitialized) {
      posthogWindow.posthog = posthog
      attachGlobalErrorHandlers(posthogWindow)
      return
    }

    posthogWindow.__posthogInitialized = true

    const apiHost = POSTHOG_HOST.startsWith("http")
      ? POSTHOG_HOST
      : POSTHOG_HOST.startsWith("/")
        ? `${window.location.origin}${POSTHOG_HOST}`
        : `https://${POSTHOG_HOST}`

    // Session recording is only useful on /quiz/results* (where we want to
    // see how users interact with their results). Disabling it by default
    // skips loading posthog-recorder.js, surveys.js, and exception-autocapture.js
    // entirely on landing/marketing routes, saving ~120KB of script weight
    // and ~1.6s of mobile main-thread script eval time per pageview.
    // Recording can be enabled at runtime by calling
    // `posthog.startSessionRecording()` from the /quiz/results client.
    const wantsRecording = pathname?.startsWith("/quiz/results") ?? false;

    posthog.init(POSTHOG_KEY, {
      api_host: apiHost,
      ui_host: "https://us.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true,
      disable_session_recording: !wantsRecording,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-mask]',
      },
      autocapture: false,
      capture_dead_clicks: false,
      // web_vitals only: field CLS/LCP/INP monitoring went dark on 2026-06-08
      // when this was `false` wholesale; network_timing stays off since that
      // was the costly part the mobile perf pass wanted to avoid.
      capture_performance: { web_vitals: true, network_timing: false },
      capture_heatmaps: false,
      // Signed report tokens (30-day paid-report access) ride in the URL;
      // without scrubbing they land in $current_url / $referrer on every
      // event and recording, readable by anyone with PostHog access.
      sanitize_properties: (props: Record<string, unknown>) => {
        for (const key of Object.keys(props)) {
          const value = props[key];
          if (typeof value === 'string' && value.includes('token=')) {
            props[key] = value.replace(/([?&]token=)[^&#]+/g, '$1REDACTED');
          }
        }
        return props;
      },
      // Drop pure "Script error." exceptions before they leave the browser.
      // They come from cross-origin scripts (extensions, third-party tags)
      // that the browser masks entirely, so they carry zero actionable
      // information and only lower the signal of the error-tracking feed
      // (~25 users/14d of pure noise as of Aug 2026). Anything with a real
      // message or stack still goes through.
      before_send: (event) => {
        if (event?.event === '$exception') {
          const props = (event.properties ?? {}) as Record<string, unknown>;
          const values = Array.isArray(props.$exception_values) ? (props.$exception_values as unknown[]) : [];
          const list = Array.isArray(props.$exception_list)
            ? (props.$exception_list as Array<{ value?: unknown }>).map((e) => e?.value)
            : [];
          const messages = [...values, ...list].filter((m): m is string => typeof m === 'string');
          if (messages.length > 0 && messages.every((m) => m.trim() === 'Script error.')) {
            return null;
          }
        }
        return event;
      },
      // disable_surveys + advanced_disable_feature_flags skip loading the
      // surveys.js and exception-autocapture.js side scripts on landing
      // pages where we don't use those features.
      disable_surveys: !wantsRecording,
      advanced_disable_feature_flags: !wantsRecording,
      debug: false,
      loaded: (client) => {
        posthogWindow.posthog = client
        attachGlobalErrorHandlers(posthogWindow)
      }
    })

    return () => {
      if (process.env.NODE_ENV === "development") {
        posthog.reset()
        delete posthogWindow.posthog
        delete posthogWindow.__posthogInitialized
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname check is for /jobs gating only; init must run once on mount, not per nav
  }, [])

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageviewTracker />
      </Suspense>
      {children}
    </PHProvider>
  )
}
